import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, UserPlus, X, Check } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Founder-only sidebar card. Silently hides itself if the current user isn't a
 * founder (endpoint returns 403). Auto-refreshes every 30s.
 */
export default function PendingApprovalsCard() {
  const [pending, setPending] = useState(null); // null = unknown, [] = loaded, non-founder = "denied"
  const [busy, setBusy] = useState({});

  const load = () => {
    api.get("/admin-auth/pending")
      .then(({ data }) => setPending(data.pending || []))
      .catch((e) => {
        if (e?.response?.status === 403) setPending("denied");
      });
  };
  useEffect(() => {
    load();
    const id = setInterval(() => { if (document.visibilityState === "visible") load(); }, 30000);
    return () => clearInterval(id);
  }, []);

  const review = async (id, status) => {
    setBusy({ ...busy, [id]: true });
    try {
      await api.patch(`/admin-auth/users/${id}/review`, { status });
      toast.success(status === "approved" ? "Approved." : "Denied.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update.");
    } finally {
      setBusy((b) => { const { [id]: _, ...rest } = b; return rest; });
    }
  };

  if (pending === "denied" || pending === null || (Array.isArray(pending) && pending.length === 0)) {
    return null;
  }

  return (
    <div data-testid="pending-approvals" className="rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/10 px-3 py-3">
      <div className="flex items-center gap-1.5 mb-2">
        <UserPlus className="h-3 w-3 text-[#a3823f]" />
        <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">
          Pending approvals
          <span className="ml-1.5 tabular-nums text-[#1e3d3a]">({pending.length})</span>
        </p>
      </div>
      <ul className="space-y-2">
        {pending.map((u) => (
          <li key={u.id} data-testid={`pending-user-${u.username}`} className="rounded-md bg-white/70 border border-[#1e3d3a]/8 p-2">
            <div className="flex items-center justify-between gap-1 mb-1">
              <span className="text-[11px] font-medium text-[#1e3d3a] truncate">{u.display_name || u.username}</span>
              <span className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/50">@{u.username}</span>
            </div>
            {u.email && <p className="text-[10px] text-[#1e3d3a]/55 truncate mb-1.5">{u.email}</p>}
            <div className="flex gap-1">
              <button
                type="button"
                data-testid={`approve-${u.username}`}
                onClick={() => review(u.id, "approved")}
                disabled={busy[u.id]}
                className="flex-1 inline-flex items-center justify-center gap-0.5 rounded-full bg-[#6b7c3a] text-white px-2 py-1 text-[9px] uppercase tracking-[0.2em] hover:bg-[#4d5a24] disabled:opacity-60 transition-colors duration-300"
              >
                {busy[u.id] ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Check className="h-2.5 w-2.5" />}
                Approve
              </button>
              <button
                type="button"
                data-testid={`deny-${u.username}`}
                onClick={() => review(u.id, "denied")}
                disabled={busy[u.id]}
                className="flex-1 inline-flex items-center justify-center gap-0.5 rounded-full border border-[#b23a48]/40 text-[#b23a48] px-2 py-1 text-[9px] uppercase tracking-[0.2em] hover:bg-[#b23a48]/10 disabled:opacity-60 transition-colors duration-300"
              >
                <X className="h-2.5 w-2.5" /> Deny
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
